import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';


const BulkActionsPanel = ({ selectedStores, onBulkUpdate, onClearSelection, stores }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [rentAdjustment, setRentAdjustment] = useState({
    type: 'percentage',
    value: ''
  });
  const [newLocation, setNewLocation] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const actionOptions = [
    { value: '', label: 'Select bulk action...' },
    { value: 'update_rent', label: 'Update Rent' },
    { value: 'change_location', label: 'Change Location' },
    { value: 'export_data', label: 'Export Data' },
    { value: 'delete_stores', label: 'Delete Stores' }
  ];

  const adjustmentTypeOptions = [
    { value: 'percentage', label: 'Percentage (%)' },
    { value: 'fixed', label: 'Fixed Amount (৳)' }
  ];

  const locationOptions = [
    { value: 'dhaka', label: 'Dhaka' },
    { value: 'chittagong', label: 'Chittagong' },
    { value: 'sylhet', label: 'Sylhet' },
    { value: 'rajshahi', label: 'Rajshahi' },
    { value: 'khulna', label: 'Khulna' },
    { value: 'barisal', label: 'Barisal' },
    { value: 'rangpur', label: 'Rangpur' },
    { value: 'mymensingh', label: 'Mymensingh' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('bn-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0
    })?.format(amount)?.replace('BDT', '৳');
  };

  const calculateNewRent = (currentRent, adjustmentType, adjustmentValue) => {
    const value = parseFloat(adjustmentValue);
    if (isNaN(value)) return currentRent;

    if (adjustmentType === 'percentage') {
      return currentRent + (currentRent * value / 100);
    } else {
      return currentRent + value;
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedStores?.length === 0) return;

    setIsProcessing(true);

    try {
      switch (bulkAction) {
        case 'update_rent':
          if (rentAdjustment?.value) {
            const updatedStores = selectedStores?.map(storeId => {
              const store = stores?.find(s => s?.id === storeId);
              const newRent = calculateNewRent(
                store?.monthlyRent, 
                rentAdjustment?.type, 
                rentAdjustment?.value
              );
              return {
                ...store,
                monthlyRent: newRent,
                rentPerSqFt: newRent / store?.size
              };
            });
            await onBulkUpdate('rent', updatedStores);
          }
          break;

        case 'change_location':
          if (newLocation) {
            const updatedStores = selectedStores?.map(storeId => {
              const store = stores?.find(s => s?.id === storeId);
              return {
                ...store,
                location: locationOptions?.find(opt => opt?.value === newLocation)?.label || newLocation
              };
            });
            await onBulkUpdate('location', updatedStores);
          }
          break;

        case 'export_data':
          const selectedStoreData = stores?.filter(store => selectedStores?.includes(store?.id));
          const csvContent = generateCSV(selectedStoreData);
          downloadCSV(csvContent, 'stores_export.csv');
          break;

        case 'delete_stores':
          if (window.confirm(`Are you sure you want to delete ${selectedStores?.length} store(s)? This action cannot be undone.`)) {
            await onBulkUpdate('delete', selectedStores);
          }
          break;

        default:
          break;
      }

      // Reset form
      setBulkAction('');
      setRentAdjustment({ type: 'percentage', value: '' });
      setNewLocation('');
      setIsExpanded(false);
    } catch (error) {
      console.error('Bulk action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const generateCSV = (storeData) => {
    const headers = ['ID', 'Name', 'Location', 'Size (sq ft)', 'Monthly Rent', 'Rent per Sq Ft', 'Contact Person', 'Phone', 'Email'];
    const rows = storeData?.map(store => [
      store?.id,
      store?.name,
      store?.location,
      store?.size,
      store?.monthlyRent,
      store?.rentPerSqFt?.toFixed(2),
      store?.contactPerson,
      store?.phone,
      store?.email
    ]);

    return [headers, ...rows]?.map(row => row?.join(','))?.join('\n');
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const getPreviewText = () => {
    if (bulkAction === 'update_rent' && rentAdjustment?.value) {
      const sampleStore = stores?.find(s => selectedStores?.includes(s?.id));
      if (sampleStore) {
        const newRent = calculateNewRent(sampleStore?.monthlyRent, rentAdjustment?.type, rentAdjustment?.value);
        return `Example: ${formatCurrency(sampleStore?.monthlyRent)} → ${formatCurrency(newRent)}`;
      }
    }
    return null;
  };

  if (selectedStores?.length === 0) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft mb-6">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="CheckSquare" size={16} className="text-primary" />
            </div>
            <div>
              <span className="font-medium text-foreground">
                {selectedStores?.length} store{selectedStores?.length > 1 ? 's' : ''} selected
              </span>
              <p className="text-sm text-muted-foreground">
                Perform bulk actions on selected stores
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
              iconPosition="right"
            >
              Bulk Actions
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              iconName="X"
              iconPosition="left"
            >
              Clear
            </Button>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-6 pt-4 border-t border-border space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Select
                label="Action"
                placeholder="Select action"
                options={actionOptions}
                value={bulkAction}
                onChange={setBulkAction}
              />

              {bulkAction === 'update_rent' && (
                <>
                  <Select
                    label="Adjustment Type"
                    options={adjustmentTypeOptions}
                    value={rentAdjustment?.type}
                    onChange={(value) => setRentAdjustment(prev => ({ ...prev, type: value }))}
                  />
                  <Input
                    label={`Adjustment ${rentAdjustment?.type === 'percentage' ? '(%)' : '(৳)'}`}
                    type="number"
                    placeholder={rentAdjustment?.type === 'percentage' ? 'e.g., 10' : 'e.g., 5000'}
                    value={rentAdjustment?.value}
                    onChange={(e) => setRentAdjustment(prev => ({ ...prev, value: e?.target?.value }))}
                  />
                </>
              )}

              {bulkAction === 'change_location' && (
                <Select
                  label="New Location"
                  placeholder="Select new location"
                  options={locationOptions}
                  value={newLocation}
                  onChange={setNewLocation}
                />
              )}
            </div>

            {getPreviewText() && (
              <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <Icon name="Info" size={16} className="text-accent" />
                  <span className="text-sm font-medium text-accent">Preview:</span>
                  <span className="text-sm text-accent">{getPreviewText()}</span>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-muted-foreground">
                This action will affect {selectedStores?.length} store{selectedStores?.length > 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsExpanded(false);
                    setBulkAction('');
                    setRentAdjustment({ type: 'percentage', value: '' });
                    setNewLocation('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant={bulkAction === 'delete_stores' ? 'destructive' : 'default'}
                  size="sm"
                  onClick={handleBulkAction}
                  loading={isProcessing}
                  disabled={!bulkAction || (bulkAction === 'update_rent' && !rentAdjustment?.value) || (bulkAction === 'change_location' && !newLocation)}
                  iconName={bulkAction === 'delete_stores' ? 'Trash2' : 'Play'}
                  iconPosition="left"
                >
                  {bulkAction === 'delete_stores' ? 'Delete Stores' : 'Apply Action'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkActionsPanel;